// apps/web/src/components/builder/model-view/CellContextMenu.tsx
// Phase 2 Session 4 — right-click context menu for the Model-tab grid.
//
// Architecture:
//   - React portal to `document.body` — escapes sticky-header and scroll
//     container overflow-hidden clipping (same pattern as StatusDropdown).
//   - `position: fixed` at click coords, shifted by viewport-edge flip.
//   - 6 actions: Copy value / Copy formula (gated) / Copy as A1 reference /
//     Reveal in tree / View in conversation (gated on conversationId) /
//     Show provenance (gated on cell.provenance presence).
//   - Close on scroll (window capture) / click-outside (mousedown) / action.

import {
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import type { GridCell } from '@arcanum-hq/shared-types';
import type { ModelViewSchemaSubset } from '@/lib/api';
import { colToA1 } from './grid-helpers';
import { formatCellValue } from './cell-style';

export interface CellContextMenuProps {
  /** Anchor screen coords from contextmenu event (clientX/clientY). */
  x: number;
  y: number;
  /** The cell being right-clicked. */
  cell: GridCell;
  /**
   * Resolved display value for the cell — passed from ModelGrid's DataCell
   * render path (via resolveValue) because GridCell itself carries no `.value`
   * field; the value lives in the parent's cellValues/inputValues caches.
   * Used by the "Copy value" action via formatCellValue.
   */
  value: unknown;
  /** Sheet name for "Copy as A1" + sheet-scoped reveal. */
  sheetName: string;
  /** Schema for formatCellValue dispatch. */
  schema: ModelViewSchemaSubset;
  /**
   * null when public viewer (DealModelView) — hides "View in conversation"
   * action. Non-negotiable per spec §4 + ADR-0003.
   */
  conversationId: string | null;
  /** Reveal-in-tree callback (ModelLayout's selectAndPulse). */
  onReveal: (target: { sheet: string; row: number; col: number }) => void;
  /** Open CellDetailPanel to provenance section (when available). */
  onShowProvenance: (target: {
    sheet: string;
    row: number;
    col: number;
  }) => void;
  /**
   * View in conversation — dispatches window event consumed by ConversationPane.
   * Optional — callers may omit when conversationId === null (public viewer).
   */
  onViewInConversation?: (target: {
    sheet: string;
    row: number;
    col: number;
  }) => void;
  /** Close handler (clicked outside, scroll, or after action fires). */
  onClose: () => void;
}

// Static literal class — Tailwind v4 no-safelist: all classes are complete
// static strings (no interpolation). Applied to every menu button.
// F-I5 (post-ship remediation, 2026-05-25): explicit focus-visible accent
// outline so keyboard nav (arrow-key cycling) shows the same green focus
// ring as the rest of the design system. Negative offset so the ring sits
// inside the row and doesn't bleed past the panel border.
const MENU_ITEM_CLASS =
  'w-full px-3 py-1.5 text-left text-xs font-mono text-text-bright hover:bg-panel-border-hi focus-visible:bg-panel-border-hi focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent focus-visible:-outline-offset-1 disabled:text-text-muted disabled:cursor-not-allowed transition-colors';

export function CellContextMenu({
  x,
  y,
  cell,
  value,
  sheetName,
  schema,
  conversationId,
  onReveal,
  onShowProvenance,
  onViewInConversation,
  onClose,
}: CellContextMenuProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({ left: x, top: y });

  // Viewport-edge flip — if the menu would clip the right or bottom edge,
  // shift it inward. Runs via useLayoutEffect so the menu measures at its
  // natural position before any paint (same pattern as StatusDropdown).
  // research/phase-2.md § 4 block P (StatusDropdown precedent).
  useLayoutEffect(() => {
    if (!panelRef.current) return;
    const rect = panelRef.current.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    let left = x;
    let top = y;
    if (left + rect.width > viewportW - 8) {
      left = Math.max(8, viewportW - rect.width - 8);
    }
    if (top + rect.height > viewportH - 8) {
      top = Math.max(8, viewportH - rect.height - 8);
    }
    setPos({ left, top });
  }, [x, y]);

  // Close on scroll — window capture so it fires regardless of which scroll
  // container receives the event. research/phase-2.md § 4 block P precedent.
  useLayoutEffect(() => {
    const onScroll = () => onClose();
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [onClose]);

  // Close on click-outside — mousedown so the check fires before the click
  // reaches any other listener (including selection on the underlying cell).
  useLayoutEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    window.addEventListener('mousedown', onMouseDown);
    return () => window.removeEventListener('mousedown', onMouseDown);
  }, [onClose]);

  // ---------------------------------------------------------------------------
  // Clipboard helper — try/catch quiet-fail per research/phase-2.md § 4 blocks
  // Q + R (CellDetailPanel.tsx:271 + SendDealLinkModal.tsx:202 precedents).
  // Always closes the menu after the attempt regardless of success.
  // ---------------------------------------------------------------------------
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard write blocked (missing permission or insecure context).
      // Quiet fail — no global toast (matches CellDetailPanel + SendDealLinkModal).
    }
    onClose();
  };

  // Action: Copy value — formatted display string (same as CellDetailPanel).
  // Uses the `value` prop (resolved via resolveValue in ModelGrid's DataCell
  // path) — GridCell has no `.value` field of its own.
  const onCopyValue = () => {
    const formatted = formatCellValue(value, cell, schema);
    void copyToClipboard(formatted);
  };

  // Action: Copy formula — raw formula string (gated on formula presence).
  const onCopyFormula = () => {
    if (!cell.formula) return;
    void copyToClipboard(cell.formula);
  };

  // Action: Copy as A1 reference — `SheetName!C5` style.
  // REUSE colToA1 from grid-helpers.ts:24 (Phase 2 research finding D2 — DO NOT
  // author a new helper). cell.col and cell.row are 1-based per GridCell schema.
  const onCopyA1 = () => {
    const a1Ref = `${sheetName}!${colToA1(cell.col)}${cell.row}`;
    void copyToClipboard(a1Ref);
  };

  // Action: Reveal in tree — fires selectAndPulse callback (ModelLayout:477).
  const onRevealAction = () => {
    onReveal({ sheet: sheetName, row: cell.row, col: cell.col });
    onClose();
  };

  // Action: View in conversation — dispatches window event consumed by
  // ConversationPane. Only rendered when conversationId !== null (public viewer
  // gate — non-negotiable per spec §4).
  const onViewAction = () => {
    if (!conversationId || !onViewInConversation) return;
    onViewInConversation({ sheet: sheetName, row: cell.row, col: cell.col });
    onClose();
  };

  // Action: Show provenance — opens CellDetailPanel to provenance section.
  // Gated on cell.provenance presence.
  const onProvenanceAction = () => {
    onShowProvenance({ sheet: sheetName, row: cell.row, col: cell.col });
    onClose();
  };

  // Provenance availability — input cells may carry provenance from the agent's
  // writeback. `cell.provenance` is undefined on non-input cells.
  const hasProvenance = Boolean(
    (cell as GridCell & { provenance?: unknown }).provenance
  );

  // F-I5: arrow-key navigation between menuitems. ArrowDown/ArrowUp cycle
  // through the focusable menu items (skips disabled). Home/End jump to
  // first/last. Mirrors WAI-ARIA menu pattern.
  const handleMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (
      e.key !== 'ArrowDown' &&
      e.key !== 'ArrowUp' &&
      e.key !== 'Home' &&
      e.key !== 'End'
    )
      return;
    const items = Array.from(
      panelRef.current?.querySelectorAll<HTMLButtonElement>(
        'button[role="menuitem"]:not([disabled])'
      ) ?? []
    );
    if (items.length === 0) return;
    e.preventDefault();
    const currentIdx = items.indexOf(
      document.activeElement as HTMLButtonElement
    );
    let nextIdx: number;
    if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = items.length - 1;
    else if (e.key === 'ArrowDown')
      nextIdx = currentIdx < 0 ? 0 : (currentIdx + 1) % items.length;
    else nextIdx = currentIdx <= 0 ? items.length - 1 : currentIdx - 1;
    items[nextIdx]?.focus();
  };

  const panel = (
    <div
      ref={panelRef}
      data-testid="cell-context-menu"
      role="menu"
      aria-label="Cell actions"
      onKeyDown={handleMenuKeyDown}
      className="bg-panel-bg border border-panel-border rounded-[3px] shadow-lg py-1 min-w-[200px]"
      style={{
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        // z-9999: above selection ring (z-[1]), sticky strips (z-2/3),
        // SheetTabs, CellDetailPanel. Below application modal overlays
        // only when those carry z-[10000]+ (none currently do).
        zIndex: 9999,
      }}
    >
      <button
        type="button"
        onClick={onCopyValue}
        className={MENU_ITEM_CLASS}
        role="menuitem"
        data-testid="ctx-copy-value"
      >
        Copy value
      </button>
      <button
        type="button"
        onClick={onCopyFormula}
        className={MENU_ITEM_CLASS}
        role="menuitem"
        disabled={!cell.formula}
        data-testid="ctx-copy-formula"
      >
        Copy formula
      </button>
      <button
        type="button"
        onClick={onCopyA1}
        className={MENU_ITEM_CLASS}
        role="menuitem"
        data-testid="ctx-copy-a1"
      >
        Copy as A1 reference
      </button>

      <div className="my-1 border-t border-panel-border" />

      <button
        type="button"
        onClick={onRevealAction}
        className={MENU_ITEM_CLASS}
        role="menuitem"
        data-testid="ctx-reveal-tree"
      >
        Reveal in tree
      </button>

      {/* Public-viewer gate — non-negotiable per spec §4 + ADR-0003.
          conversationId is null on the /deal/:shareId public route. */}
      {conversationId !== null && (
        <button
          type="button"
          onClick={onViewAction}
          className={MENU_ITEM_CLASS}
          role="menuitem"
          data-testid="ctx-view-in-conversation"
        >
          View in conversation
        </button>
      )}

      {/* Provenance gate — only when the cell carries provenance data. */}
      {hasProvenance && (
        <button
          type="button"
          onClick={onProvenanceAction}
          className={MENU_ITEM_CLASS}
          role="menuitem"
          data-testid="ctx-show-provenance"
        >
          Show provenance
        </button>
      )}
    </div>
  );

  return createPortal(panel, document.body);
}
